import React, { useEffect, useState } from 'react'
import { ALL_INVITES, DELETE_INVITE, RESEND_INVITE } from 'api/InvitesConstants'
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, MenuItem, Chip, TextField, Typography, Box, Tooltip } from '@mui/material'
import commonStyles from 'styles/CommonStyles'
import EnhancedTableHead from "components/common/EnhancedTableHead2";
import ReuseConfirmationDialog from 'components/common/ReuseConfirmationDialog2'
import SearchBar from 'components/common/SearchBar';
import AppActions from 'components/app/actions';

export default () => {
    const [invites, setInvites] = useState([]);
    const [selectedInvite, setSelectedInvite] = useState({});
    const [editInviteDialogOpen, setEditInviteDialogOpen] = useState(false);
    const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [page, setPage] = useState(0)
    const [order, setOrder] = useState('desc')
    const [orderBy, setOrderBy] = useState('sent_date')
    const [filters, setFilters] = useState({
        inviteStatus: 'ACTIVE',
        role: 'all',
        type: 'all',
    })
    const [search, setSearch] = useState('');
    const [totalCount, setTotalCount] = useState(0);
    const { setToastMessage } = AppActions();

    const commonClasses = commonStyles();

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value)
        setPage(0)
    }

    const handleEditInviteDialogOpen = (invite) => {
        setSelectedInvite(invite);
        setEditInviteDialogOpen(true);
    }

    const handleEditInviteDialogClose = () => {
        setEditInviteDialogOpen(false);
    }

    const handleDeleteInvite = (invite) => {
        setSelectedInvite(invite);
        setDeleteConfirmationDialogOpen(true);
    }

    const fetchInvites = (limit, offset, filters, order, orderBy, search) => {
        fetch(ALL_INVITES,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    "limit": limit,
                    "offset": offset,
                    "search": search,
                    "filters": {
                        "role": filters.role,
                        "status": filters.inviteStatus,
                        "type": filters.type
                    },
                    "order": order,
                    "order_by": orderBy
                })
            })
            .then(res => res.json())
            .then(data => {
                setInvites(data.invites || []);
                setTotalCount(data.total_count);
            })
            .catch(err => {
                console.log(err);
            })
    }

    const resendInvite = (invite) => {
        fetch(`${RESEND_INVITE}/${invite.id}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setToastMessage({
                        message: 'Invite sent successfully',
                        type: 'success',
                    })
                } else {
                    throw new Error(data.message);
                }
            })
            .catch(err => {
                console.error(err);
                setToastMessage({
                    message: err.message || 'An error occurred while resending invite',
                    type: 'error',
                })
            })
    }

    const getInviteLink = (invite) => {
        return `${window.location.origin}/invitation?token=${invite.token}`
    }

    const deleteInvite = (invite) => {
        fetch(`${DELETE_INVITE}/${invite.id}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setInvites(invites.filter(invite => invite.id !== selectedInvite.id));
                    setToastMessage({
                        message: 'Invite deleted successfully',
                        type: 'success',
                    })
                } else {
                    throw new Error(data.message);
                }
            })
            .catch(err => {
                console.error(err);
                setToastMessage({
                    message: err.message || 'An error occurred while deleting invite',
                    type: 'error',
                })
            })
    }

    const roleFilterOptions = [
        { value: 'all', label: 'All' },
        { value: 'ADMIN', label: 'Admin' },
        { value: 'USER', label: 'User' },
    ];

    const inviteStatusFilterOptions = [
        { value: 'all', label: 'All' },
        { value: 'ACTIVE', label: 'Active' },
        { value: 'REGISTERED', label: 'Registered' },
        { value: 'EXPIRED', label: 'Expired' },
        { value: 'DELETED', label: 'Deleted' },
    ]

    const inviteTypeFilterOptions = [
        { value: 'all', label: 'All' },
        { value: 'LINK', label: 'Link' },
        { value: 'EMAIL', label: 'Email' },
    ]

    const headCells = [
        { id: 'email', disablePadding: false, label: 'Email/Token', width: 120 },
        { id: 'role', disablePadding: false, label: 'Role', width: 120 },
        { id: 'status', disablePadding: false, label: 'Status', width: 120 },
        { id: 'sent_date', disablePadding: false, label: 'Sent On', width: 120 },
        { id: 'expiry_date', disablePadding: false, label: 'Expires On', width: 120 },
        { id: 'created_by', disablePadding: false, label: 'Created By', width: 180, disableSort: true },
        { id: 'actions', disablePadding: true, label: 'Actions', width: 200, disableSort: true },
    ];

    useEffect(() => {
        fetchInvites(rowsPerPage, page * rowsPerPage, filters, order, orderBy, search);
    }, [page, rowsPerPage, order, orderBy])

    useEffect(() => {
        setPage(0);
        fetchInvites(rowsPerPage, page * rowsPerPage, filters, order, orderBy, search);
    }, [filters, search])

    return (
        <>
            <div style={{ marginTop: '2rem' }}>
                {inviteTypeFilterOptions.map((option) => (
                    <Chip key={option.value} label={option.label} onClick={() => setFilters({ ...filters, type: option.value })} color={filters.type === option.value ? 'primary' : 'default'} style={{ marginRight: '0.5rem' }} />
                ))}
            </div>
            <div style={{ marginTop: '1rem' }}>
                {inviteStatusFilterOptions.map((option) => (
                    <Chip key={option.value} label={option.label} onClick={() => setFilters({ ...filters, inviteStatus: option.value })} color={filters.inviteStatus === option.value ? 'primary' : 'default'} style={{ marginRight: '0.5rem' }} />
                ))}
            </div>
            <SearchBar handleSearch={(search) => setSearch(search)} helpText="Search by name or email" />
            <TableContainer style={{ marginTop: '1rem' }}>
                <Table>
                    <EnhancedTableHead
                        columns={headCells}
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                        <TableRow>
                            <TableCell />
                            <TableCell>
                                <TextField
                                    size="small"
                                    select
                                    label="Role"
                                    value={filters.role}
                                    onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                                    style={{ width: '100%' }}
                                >
                                    {roleFilterOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </TableCell>
                            <TableCell colSpan={5} />
                        </TableRow>
                        {invites.map((invite) => (
                            <TableRow key={invite.id}>
                                <TableCell>{invite.email || invite.token}</TableCell>
                                <TableCell>{invite.role}</TableCell>
                                <TableCell>{invite.status}</TableCell>
                                <TableCell>{invite.sent_on}</TableCell>
                                <TableCell>{invite.expires_on}</TableCell>
                                <TableCell>{invite.created_by}</TableCell>
                                <TableCell>
                                    <div className={commonClasses.tableActions}>
                                        {invite.status === 'ACTIVE' && (
                                            <>
                                                {invite.type === 'EMAIL' && (
                                                    <Button size="small" variant='outlined' onClick={() => resendInvite(invite)}>Resend</Button>
                                                )}
                                                {invite.type === 'LINK' && (
                                                    <Button size="small" variant='outlined' onClick={() => {
                                                        navigator.clipboard.writeText(getInviteLink(invite));
                                                        setToastMessage({
                                                            message: 'Invite link copied to clipboard',
                                                            type: 'success',
                                                        })
                                                    }}
                                                    >Copy Link</Button>
                                                )}
                                                <Button size="small" variant='outlined' color="secondary" onClick={() => handleDeleteInvite(invite)}>Delete</Button>
                                            </>
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>
            <ReuseConfirmationDialog
                open={deleteConfirmationDialogOpen}
                handleConfirm={() => {
                    deleteInvite(selectedInvite);
                    setDeleteConfirmationDialogOpen(false)
                }}
                handleCancel={setDeleteConfirmationDialogOpen}
                title="Delete Invite"
            >
                <>
                    <Typography variant="body1" paragraph> Are you sure you want to delete this invite?</Typography>
                    <Typography variant="body1" paragraph> This action cannot be undone.</Typography>
                </>
            </ReuseConfirmationDialog>
        </>
    )
}
